import React from 'react';

import SubmitButton from '@common_components/buttons/submitButton.component';
import { GroupedOption } from '@common_components/inputs/selects/selectInput.component';
import AdditionalIndicatorSection from './chartAdditionalIndicatorSection.component';
import MainDataSection from './chartMainDataSection.component';
import ReadSection from './chartReadSection.component';
import TargetSection from './chartTargetSection.component';
import { getReadCategory } from './chartReadRange.utils';
import { getAddSettingValidation } from './chartAddSettingValidation.utils';
import {
  chartTypeOptions,
  historyOptions,
  readEndOptions,
  readStartOptions,
  targetTypeOptions,
} from './chartSectionOptions.constants';
import type { AddChartSettingCardProps } from './chartSectionList.interfaces';
import type { ChartReadEnd } from 'interfaces/chartOfSection.interface';

const flattenOptionIds = ( groups: GroupedOption[] ): string[] => {
  return groups.flatMap( group => { return group.options.map( option => { return option.id; } ); } );
};

const AddChartSettingCard: React.FC<AddChartSettingCardProps> = ( {
  t: translate,
  newSetting: newSettingProp,
  hasTargetValue: hasTargetValueProp,
  isEditingExisting,
  originalSetting = null,
  chartUnitOptions,
  managerOptions,
  themeOptions,
  onChangeField,
  onCreate,
  onBack,
} ) => {
  const fetchChartTypes = (): Promise<GroupedOption[]> => {
    const translatedOptions = chartTypeOptions.map( group => {
      return {
        ...group,
        options: group.options.map( option => {
          return {
            ...option,
            label: translate( `translation:${option.id}` ),
          };
        } ),
      };
    } );
    return Promise.resolve( translatedOptions );
  };

  const fetchChartUnits = (): Promise<GroupedOption[]> => {
    const noneOption: GroupedOption = {
      options: [ { id: '__none__', label: translate( 'translation:none' ) } ],
    };
    return Promise.resolve( [ noneOption, ...chartUnitOptions ] );
  };

  const fetchManagers = (): Promise<GroupedOption[]> => {
    return Promise.resolve( managerOptions );
  };

  const fetchTargetTypes = (): Promise<GroupedOption[]> => {
    const translatedOptions = targetTypeOptions.map( group => {
      return {
        ...group,
        options: group.options.map( option => {
          return {
            ...option,
            label: translate( `translation:${option.id}` ),
          };
        } ),
      };
    } );
    return Promise.resolve( translatedOptions );
  };

  const fetchAlertThemes = (): Promise<GroupedOption[]> => {
    return Promise.resolve( themeOptions );
  };

  const fetchTicketThemes = (): Promise<GroupedOption[]> => {
    return Promise.resolve( themeOptions );
  };

  const fetchReadStartOptions = (): Promise<GroupedOption[]> => {
    const translatedOptions = readStartOptions.map( group => {
      return {
        ...group,
        options: group.options.map( option => {
          return { ...option, label: translate( `translation:${option.label}` ) };
        } ),
      };
    } );
    return Promise.resolve( translatedOptions );
  };

  const fetchReadEndOptions = (): Promise<GroupedOption[]> => {
    const category = getReadCategory( newSettingProp.readStart );

    const baseOptions = category
      ? readEndOptions.map( group => {
        return {
          ...group,
          options: group.options.filter( option => {
            return getReadCategory( option.id as ChartReadEnd ) === category;
          } ),
        };
      } ).filter( group => { return group.options.length > 0; } )
      : readEndOptions;

    const translatedOptions = baseOptions.map( group => {
      return {
        ...group,
        options: group.options.map( option => {
          return { ...option, label: translate( `translation:${option.label}` ) };
        } ),
      };
    } );

    return Promise.resolve( translatedOptions );
  };

  const fetchHistoryOptions = (): Promise<GroupedOption[]> => {
    const translatedOptions = historyOptions.map( group => {
      return {
        ...group,
        options: group.options.map( option => {
          return {
            ...option,
            label: translate( `translation:${option.label}` ),
          };
        } ),
      };
    } );
    return Promise.resolve( translatedOptions );
  };

  const themesRefreshTrigger = themeOptions.flatMap( g => { return g.options; } ).map( o => { return o.id; } ).join( ',' );

  const chartTypeIds = React.useMemo( () => {
    return flattenOptionIds( chartTypeOptions );
  }, [] );

  const chartUnitIds = React.useMemo( () => {
    const noneOption: GroupedOption = {
      options: [ { id: '__none__', label: '__none__' } ],
    };
    return flattenOptionIds( [ noneOption, ...chartUnitOptions ] );
  }, [ chartUnitOptions ] );

  const managerIds = React.useMemo( () => {
    return flattenOptionIds( managerOptions );
  }, [ managerOptions ] );

  const targetTypeIds = React.useMemo( () => {
    return flattenOptionIds( targetTypeOptions );
  }, [] );

  const readStartIds = React.useMemo( () => {
    return flattenOptionIds( readStartOptions );
  }, [] );

  const readEndIds = React.useMemo( () => {
    const category = getReadCategory( newSettingProp.readStart );
    const filteredByCategory = category
      ? readEndOptions.map( group => {
        return {
          ...group,
          options: group.options.filter( option => {
            return getReadCategory( option.id as ChartReadEnd ) === category;
          } ),
        };
      } ).filter( group => { return group.options.length > 0; } )
      : readEndOptions;

    return flattenOptionIds( filteredByCategory );
  }, [ newSettingProp.readStart ] );

  const themeIds = React.useMemo( () => {
    return flattenOptionIds( themeOptions );
  }, [ themeOptions ] );

  const { readCategoryMismatch, isSaveDisabled } = getAddSettingValidation( newSettingProp, hasTargetValueProp, {
    chartTypeIds,
    chartUnitIds,
    managerIds,
    targetTypeIds,
    readStartIds,
    readEndIds,
    themeIds,
  } );

  const isLocallyUnchanged = React.useMemo( () => {
    if ( !isEditingExisting || !originalSetting ) { return false; }
    const keys: ( keyof typeof newSettingProp )[] = [
      'title', 'dataName', 'chartUnitId', 'managerId', 'type', 'color',
      'targetName', 'targetType', 'targetColor', 'targetValue',
      'createTicketOnTargetMiss', 'ticketCreationHour', 'ticketTheme',
      'sendAlertTime', 'alertThemeId', 'displayMin', 'displayMax', 'deltaMinMax',
      'readStart', 'readEnd', 'additionalIndicator', 'hasTarget',
      'incrementValue', 'incrementFrequencyDays',
    ];
    const normalize = ( val: unknown ): string => { return ( val === null || val === undefined ) ? '' : String( val ); };
    return keys.every( key => {
      return normalize( newSettingProp[ key as keyof typeof newSettingProp ] ) === normalize( originalSetting[ key as keyof typeof originalSetting ] );
    } );
  }, [ newSettingProp, originalSetting, isEditingExisting ] );

  return (
    <div className="mb-3 p-2 border-2 border-blue-400 rounded-xl bg-blue-50 shadow-sm">
      <p className="text-xs font-medium text-gray-700 mb-2">
        {translate( isEditingExisting ? 'translation:edit_chart_setting' : 'translation:add_chart_setting' )}
      </p>
      <div className="grid grid-cols-2 gap-3 mb-2">
        <MainDataSection
          t={translate}
          newSetting={newSettingProp}
          onChangeField={onChangeField}
          fetchChartUnits={fetchChartUnits}
          fetchManagers={fetchManagers}
          fetchChartTypes={fetchChartTypes}
        />
        <TargetSection
          t={translate}
          newSetting={newSettingProp}
          hasTargetValue={hasTargetValueProp}
          onChangeField={onChangeField}
          isEditingExisting={isEditingExisting}
          fetchTargetTypes={fetchTargetTypes}
          fetchTicketThemes={fetchTicketThemes}
          fetchAlertThemes={fetchAlertThemes}
          themesRefreshTrigger={themesRefreshTrigger}
        />
        <ReadSection
          t={translate}
          newSetting={newSettingProp}
          onChangeField={onChangeField}
          fetchReadStartOptions={fetchReadStartOptions}
          fetchReadEndOptions={fetchReadEndOptions}
        />
        <AdditionalIndicatorSection
          t={translate}
          newSetting={newSettingProp}
          onChangeField={onChangeField}
          fetchHistoryOptions={fetchHistoryOptions}
        />
      </div>
      {readCategoryMismatch && (
        <p className="text-[11px] text-red-600 mb-1">
          {translate( 'translation:read_range_incompatible' )}
        </p>
      )}
      <div className="flex flex-col gap-1">
        <div className="flex justify-end gap-2">
          <SubmitButton
            label={translate( 'common:cancel' )}
            onClick={onBack}
            disable={false}
            width="w-auto"
            bgColor="bg-white"
            textColor="text-gray-600"
            hoverBorderColor="group-hover:border-gray-400"
            bgColorHover="group-hover:bg-gray-100"
            textColorHover="group-hover:text-gray-800"
            textSize="text-xs"
          />
          <SubmitButton
            label={translate( isEditingExisting ? 'common:save' : 'common:add' )}
            onClick={onCreate}
            disable={isSaveDisabled || ( isEditingExisting ? isLocallyUnchanged : false )}
            width="w-auto"
            bgColor={isSaveDisabled || ( isEditingExisting ? isLocallyUnchanged : false ) ? 'bg-green-100' : 'bg-green-500'}
            textColor={isSaveDisabled || ( isEditingExisting ? isLocallyUnchanged : false ) ? 'text-green-300' : 'text-white'}
            hoverBorderColor={isSaveDisabled || ( isEditingExisting ? isLocallyUnchanged : false ) ? '' : 'group-hover:border-green-600'}
            bgColorHover={isSaveDisabled || ( isEditingExisting ? isLocallyUnchanged : false ) ? '' : 'group-hover:bg-green-600'}
            textColorHover={isSaveDisabled || ( isEditingExisting ? isLocallyUnchanged : false ) ? '' : 'group-hover:text-white'}
            textSize="text-xs font-medium"
          />
        </div>
      </div>
    </div>
  );
};

export default AddChartSettingCard;
